import apiClient from "./apiClient";
import { stripRichText } from "../utils/richText";

/**
 * Unwrap page payload — public API returns the page object directly.
 */
export function normalizeContentPage(data) {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.sections)) return data;
  if (data.data && Array.isArray(data.data.sections)) return data.data;
  return data;
}

/**
 * Fetch the home page — GET /api/content/pages/home
 */
export async function fetchHomePage() {
  return fetchContentPage("home");
}

/**
 * Fetch global layout — GET /api/content/pages/global
 */
export async function fetchGlobalPage() {
  return fetchContentPage("global");
}

/**
 * Fetch the about page — GET /api/content/pages/about
 */
export async function fetchAboutPage() {
  return fetchContentPage("about");
}

/**
 * Fetch the services page — GET /api/content/pages/services
 */
export async function fetchServicesPage() {
  return fetchContentPage("services");
}

/**
 * Service page slugs — each maps to GET /api/content/pages/{slug}
 */
export const SERVICE_PAGE_SLUGS = [
  "design",
  "development",
  "engineering",
  "transformation",
  "ai",
  "infrastructure",
];

/**
 * Fetch a service page by slug — GET /api/content/pages/{slug}
 */
export async function fetchServicePage(slug) {
  const normalized = String(slug || "").trim().toLowerCase();
  if (!SERVICE_PAGE_SLUGS.includes(normalized)) {
    throw new Error(`Unknown service page slug: "${slug}"`);
  }
  return fetchContentPage(normalized);
}

export async function fetchDesignPage() {
  return fetchServicePage("design");
}

/**
 * Fetch the development page — GET /api/content/pages/development
 */
export async function fetchDevelopmentPage() {
  return fetchServicePage("development");
}

export async function fetchEngineeringPage() {
  return fetchServicePage("engineering");
}

export async function fetchTransformationPage() {
  return fetchServicePage("transformation");
}

export async function fetchAiPage() {
  return fetchServicePage("ai");
}

export async function fetchInfrastructurePage() {
  return fetchServicePage("infrastructure");
}

/**
 * Industry page slugs — each maps to GET /api/content/pages/{slug}
 */
export const INDUSTRY_PAGE_SLUGS = [
  "mobility",
  "healthcare",
  "retail-ecommerce",
  "fintech",
  "travel-hospitality",
  "high-tech-software",
  "telecom-media",
  "real-estate-construction",
  "ai-data-intelligence",
];

export async function fetchIndustryPage(slug) {
  const normalized = String(slug || "").trim().toLowerCase();
  if (!INDUSTRY_PAGE_SLUGS.includes(normalized)) {
    throw new Error(`Unknown industry page slug: "${slug}"`);
  }
  return fetchContentPage(normalized);
}

export async function fetchMobilityPage() {
  return fetchIndustryPage("mobility");
}

export async function fetchHealthcareIndustryPage() {
  return fetchIndustryPage("healthcare");
}

export async function fetchRetailEcommercePage() {
  return fetchIndustryPage("retail-ecommerce");
}

export async function fetchFinTechPage() {
  return fetchIndustryPage("fintech");
}

export async function fetchTravelHospitalityPage() {
  return fetchIndustryPage("travel-hospitality");
}

export async function fetchHighTechSoftwarePage() {
  return fetchIndustryPage("high-tech-software");
}

export async function fetchTelecomMediaPage() {
  return fetchIndustryPage("telecom-media");
}

export async function fetchRealEstateConstructionPage() {
  return fetchIndustryPage("real-estate-construction");
}

export async function fetchAiDataIntelligencePage() {
  return fetchIndustryPage("ai-data-intelligence");
}

/**
 * @deprecated Industries overview page removed — use fetchIndustryPage(slug)
 */
export async function fetchIndustriesPage() {
  return fetchHealthcareIndustryPage();
}

/**
 * Fetch the insights page — GET /api/content/pages/insights
 */
export async function fetchInsightsPage() {
  return fetchContentPage("insights");
}

/**
 * Fetch the work page — GET /api/content/pages/work
 */
export async function fetchWorkPage() {
  return fetchContentPage("work");
}

/**
 * Fetch the careers page — GET /api/content/pages/careers
 */
export async function fetchCareersPage() {
  return fetchContentPage("careers");
}

/**
 * Fetch the contact page — GET /api/content/pages/contact
 */
export async function fetchContactPage() {
  return fetchContentPage("contact");
}

/**
 * @param {string} slug - e.g. "home"
 * @returns {Promise<object>} Page object from GET /api/content/pages/{slug}
 */
export async function fetchContentPage(slug) {
  const { data } = await apiClient.get(`/api/content/pages/${slug}`);
  const page = normalizeContentPage(data);
  if (!page?.sections) {
    throw new Error(`Invalid page response for "${slug}"`);
  }
  return page;
}

/** @deprecated Use fetchContentPage */
export async function fetchContentByPage(slug) {
  return fetchContentPage(slug);
}

/**
 * Find a section by key on a page object.
 */
export function findSection(page, key) {
  if (!page?.sections) return null;
  return page.sections.find((s) => s.key === key) ?? null;
}

/**
 * Primary media URL from a section, resolved for the browser.
 */
export function getSectionPrimaryMediaUrl(section, resolveMediaUrl) {
  const path = section?.media?.primary?.url;
  return path ? resolveMediaUrl(path) : "";
}

/**
 * Build cards array from a cards-type section (card_0 … card_n).
 */
export function getCardsFromSection(section, count = 6) {
  if (!section?.fields) return [];
  return Array.from({ length: count }, (_, i) => ({
    title: section.fields[`card_${i}_title`] ?? "",
    description: section.fields[`card_${i}_description`] ?? "",
  }));
}

/**
 * Blueprint cards may include card_N_label (mission/vision cards).
 */
export function getBlueprintCardsFromSection(section, count = 5) {
  if (!section?.fields) return [];
  return Array.from({ length: count }, (_, i) => ({
    label: section.fields[`card_${i}_label`] ?? "",
    title: section.fields[`card_${i}_title`] ?? "",
    description: section.fields[`card_${i}_description`] ?? "",
  }));
}

/**
 * Build tags array from a tags-type section (tag_0, tag_1, …).
 */
export function getTagsFromSection(section) {
  if (!section?.fields) return [];
  return Object.keys(section.fields)
    .filter((key) => /^tag_\d+$/.test(key))
    .sort(
      (a, b) =>
        Number(a.split("_")[1]) - Number(b.split("_")[1])
    )
    .map((key) => section.fields[key])
    .filter(Boolean);
}

/**
 * Build stats array from a stats-type section (stat_0_end, stat_0_suffix, …).
 */
export function getStatsFromSection(section) {
  if (!section?.fields) return [];
  const stats = [];
  for (let i = 0; i < 20; i++) {
    const label = section.fields[`stat_${i}_label`];
    const end = section.fields[`stat_${i}_end`];
    if (!label && (end == null || end === "")) break;
    stats.push({
      end: Number(end) || 0,
      suffix: section.fields[`stat_${i}_suffix`] ?? "",
      label: label ?? "",
    });
  }
  return stats;
}

export const HOME_SECTION_KEYS = [
  "hero",
  "welaunch",
  "capabilities",
  "industries",
  "whyus",
  "portfolio",
  "future",
  "liftoff",
];

export const ABOUT_SECTION_KEYS = [
  "about-banner",
  "getting-started",
  "blueprint",
  "ceo",
  "extra",
];

export const SERVICES_SECTION_KEYS = [
  "services-hero",
  "services-capabilities",
  "extra",
];

export const SERVICE_SECTION_KEYS = [
  "development-banner",
  "what-we-solve",
  "whats-included",
  "hosting-plans",
  "dev-process",
  "whyus",
];

/** @deprecated Use SERVICE_SECTION_KEYS */
export const DEVELOPMENT_SECTION_KEYS = SERVICE_SECTION_KEYS;

export const INDUSTRY_SECTION_KEYS = [
  "industries-banner",
  "challenges",
  "what-we-build",
  "relevant-services",
  "enables",
  "need",
];

/** @deprecated Use INDUSTRY_SECTION_KEYS */
export const INDUSTRIES_SECTION_KEYS = INDUSTRY_SECTION_KEYS;

export const INSIGHTS_SECTION_KEYS = [
  "insight-banner",
  "articles",
  "have-a-project",
];

export const WORK_SECTION_KEYS = [
  "work-banner",
  "trusted",
  "impact",
  "work-projects",
];

export const CAREERS_SECTION_KEYS = [
  "careers-banner",
  "open-positions",
  "culture",
  "cant-see",
];

export const CONTACT_SECTION_KEYS = [
  "contact-banner",
  "contact-info",
  "contact-cta",
];

/**
 * List items from item_0, item_1, …
 */
export function getListItemsFromSection(section) {
  if (!section?.fields) return [];
  const items = [];
  for (let i = 0; i < 20; i++) {
    const item = section.fields[`item_${i}`];
    if (!item) break;
    items.push(item);
  }
  return items;
}

/**
 * Hosting pricing plans from plan_N_* fields.
 */
export function getHostingPlansFromSection(section, count = 3) {
  if (!section?.fields) return { popularBadge: "", plans: [] };
  const fields = section.fields;
  const popularBadge = fields.popular_badge ?? "";
  const plans = [];

  for (let i = 0; i < count; i++) {
    const name = fields[`plan_${i}_name`];
    if (!name) continue;
    const features = [];
    for (let j = 0; j < 20; j++) {
      const feature = fields[`plan_${i}_feature_${j}`];
      if (!feature) break;
      features.push(feature);
    }
    const popularRaw = fields[`plan_${i}_popular`];
    plans.push({
      name,
      price: fields[`plan_${i}_price`] ?? "",
      popular: popularRaw === "true" || popularRaw === true,
      billing: fields[`plan_${i}_billing`] ?? "",
      buttonText: fields[`plan_${i}_buttonText`] ?? "",
      buttonLink: fields[`plan_${i}_buttonLink`] ?? "",
      features,
    });
  }

  return { popularBadge, plans };
}

/**
 * Process steps from step_N_id, step_N_title, step_N_description.
 */
export function getProcessStepsFromSection(section, count = 5) {
  if (!section?.fields) return [];
  const steps = [];
  for (let i = 0; i < count; i++) {
    const title = section.fields[`step_${i}_title`];
    const desc = section.fields[`step_${i}_description`];
    if (!title && !desc) continue;
    steps.push({
      id: section.fields[`step_${i}_id`] ?? "",
      title: title ?? "",
      desc: desc ?? "",
    });
  }
  return steps;
}

/**
 * Service capability cards (category, items, highlight, buttons).
 */
function normalizeServiceCardButtonLink(category, buttonLink) {
  const categoryText = String(category ?? "")
    .replace(/<[^>]*>/g, "")
    .trim()
    .toLowerCase();

  if (categoryText.includes("design")) return "/design";
  if (categoryText.includes("development")) return "/development";
  if (categoryText.includes("engineering")) return "/engineering";
  if (categoryText.includes("transformation")) return "/transformation";
  if (categoryText === "ai" || categoryText.includes("artificial")) return "/ai";
  if (
    categoryText.includes("infrastructure") ||
    categoryText.includes("cloud")
  ) {
    return "/infrastructure";
  }

  const link = String(buttonLink ?? "").trim();
  if (!link) return "";
  if (/^https?:\/\//i.test(link)) return link;
  // Old /service/... links → flat paths
  const legacy = link.match(/^\/?service\/([a-z0-9-]+)/i);
  if (legacy) return `/${legacy[1].toLowerCase()}`;
  return link.startsWith("/") ? link : `/${link}`;
}

export function getServiceCardsFromSection(section, count = 6) {
  if (!section?.fields) return [];
  const fields = section.fields;
  const cards = [];

  for (let i = 0; i < count; i++) {
    const category = fields[`card_${i}_category`];
    const title = fields[`card_${i}_title`];
    if (!category && !title) continue;

    const items = [];
    for (let j = 0; j < 10; j++) {
      const item = fields[`card_${i}_item_${j}`];
      if (!item) break;
      items.push(item);
    }
    const highlightRaw = fields[`card_${i}_highlight`];
    const rawButtonLink = fields[`card_${i}_buttonLink`] ?? "";
    cards.push({
      category: category ?? "",
      title: title ?? "",
      description: fields[`card_${i}_description`] ?? "",
      highlight: highlightRaw === "true" || highlightRaw === true,
      buttonText: fields[`card_${i}_buttonText`] ?? "",
      buttonLink: normalizeServiceCardButtonLink(category, rawButtonLink),
      items,
    });
  }

  return cards;
}

/**
 * Link group from footer-style fields (e.g. company_heading, company_link_0_text).
 */
export function getLinkGroupFromFields(fields, groupPrefix) {
  if (!fields) return { heading: "", links: [] };
  const heading = fields[`${groupPrefix}_heading`] ?? "";
  const links = [];
  for (let i = 0; i < 20; i++) {
    const text = fields[`${groupPrefix}_link_${i}_text`];
    const url = fields[`${groupPrefix}_link_${i}_url`];
    if (!text && !url) break;
    if (text && url) links.push({ text, url });
  }
  return { heading, links };
}

/**
 * Footer social links from social_N_platform / social_N_label / social_N_url / social_N_icon fields.
 */
export function getSocialsFromFields(fields) {
  if (!fields) return [];
  const byIndex = new Map();

  Object.keys(fields).forEach((key) => {
    const match = /^social_(\d+)_(platform|label|url|icon)$/i.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2].toLowerCase();
    if (!byIndex.has(index)) byIndex.set(index, { index });
    byIndex.get(index)[suffix] = fields[key] ?? "";
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, data]) => {
      const label = stripRichText(data.label || "");
      const platformRaw = stripRichText(data.platform || "").toLowerCase();
      return {
        index: data.index,
        platform: platformRaw,
        label,
        url: stripRichText(data.url || ""),
        // Only treat real media paths as custom icons (avoid text values leaking into icon).
        icon: isSocialIconValue(data.icon) ? String(data.icon).trim() : "",
      };
    })
    .filter((item) => item.url);
}

function isSocialIconValue(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  if (/^https?:\/\//i.test(s)) return true;
  if (s.startsWith("/") || s.startsWith("uploads/") || s.startsWith("data:")) return true;
  // Reject plain words like "twitter" / labels that may have been saved into icon by mistake.
  if (!s.includes("/") && !s.includes(".")) return false;
  return true;
}

const ARTICLE_FIELD_SUFFIXES = [
  "slug",
  "category",
  "title",
  "desc",
  "image",
  "author",
  "date",
  "avatar",
  "role",
];

/**
 * Insight articles from article_N_* fields on an articles-type section.
 */
export function getArticlesFromSection(section) {
  if (!section?.fields) return [];
  const fields = section.fields;
  const byIndex = new Map();

  Object.keys(fields).forEach((key) => {
    const match = /^article_(\d+)_(.+)$/.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[suffix] = fields[key] ?? "";
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, data]) => {
      const article = {};
      ARTICLE_FIELD_SUFFIXES.forEach((suffix) => {
        article[suffix] = data[suffix] ?? "";
      });
      return article;
    })
    .filter((article) => article.slug || article.title);
}

/**
 * Category filter labels from filter_N fields.
 */
export function getArticleFiltersFromSection(section) {
  if (!section?.fields) return [];
  return Object.keys(section.fields)
    .filter((key) => /^filter_\d+$/.test(key))
    .sort(
      (a, b) =>
        Number(a.split("_")[1]) - Number(b.split("_")[1])
    )
    .map((key) => section.fields[key])
    .filter(Boolean);
}

function parseWorkProjectFromFields(data) {
  const services = [];
  for (let i = 0; i < 3; i++) {
    const item = data[`service_${i}`];
    if (item) services.push(item);
  }

  const otherResources = [];
  for (let i = 0; i < 3; i++) {
    const item = data[`other_resource_${i}`];
    if (item) otherResources.push(item);
  }

  const tech = [];
  for (let i = 0; i < 6; i++) {
    const item = data[`tech_${i}`];
    if (item) tech.push(item);
  }

  const featuredRaw = data.featured;
  const featured = featuredRaw === "true" || featuredRaw === true;

  return {
    slug: data.slug ?? "",
    tag: data.tag ?? "",
    title: data.title ?? "",
    desc: data.desc ?? "",
    image: data.image ?? "",
    featured,
    services,
    intro: data.intro ?? "",
    intro_image: data.intro_image ?? "",
    intro_image_caption: data.intro_image_caption ?? "",
    quote_text: data.quote_text ?? "",
    quote_author: data.quote_author ?? "",
    mid_body: data.mid_body ?? "",
    software_tools_heading: data.software_tools_heading ?? "Software and tools",
    software_tools_body: data.software_tools_body ?? "",
    other_resources_heading:
      data.other_resources_heading ?? "Other resources",
    other_resources: otherResources,
    content_image_2: data.content_image_2 ?? "",
    content_image_2_caption: data.content_image_2_caption ?? "",
    post_image_body: data.post_image_body ?? "",
    conclusion_heading: data.conclusion_heading ?? "Conclusion",
    conclusion: data.conclusion ?? "",
    client: data.client ?? "",
    tech,
    connect_text: data.connect_text ?? "Let's connect",
    connect_link: data.connect_link ?? "/contact",
  };
}

/**
 * All work projects from project_N_* fields on a projects-type section.
 */
export function getWorkProjectsFromSection(section) {
  if (!section?.fields) return [];
  const fields = section.fields;
  const byIndex = new Map();

  Object.keys(fields).forEach((key) => {
    const match = /^project_(\d+)_(.+)$/.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[suffix] = fields[key] ?? "";
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, data]) => parseWorkProjectFromFields(data))
    .filter((project) => project.slug || project.title);
}

/**
 * All projects for the More work grid.
 */
export function getMoreWorkFromSection(section) {
  if (!section?.fields) {
    return { title: "More work", loadMoreText: "Load more", projects: [] };
  }

  const fields = section.fields;
  return {
    title: fields.list_title ?? "More work",
    loadMoreText: fields.load_more_text ?? "Load more",
    projects: getWorkProjectsFromSection(section),
  };
}

/**
 * Featured projects for the Selected projects hero (up to 3: 1 large + 2 small).
 */
export function getSelectedWorkProjectsFromSection(section) {
  if (!section?.fields) {
    return { title: "Selected projects", projects: [] };
  }

  const fields = section.fields;
  const maxRaw = Number(fields.featured_max);
  const max = [3, 4, 5].includes(maxRaw) ? maxRaw : 5;
  const all = getWorkProjectsFromSection(section);
  const featured = all.filter((p) => p.featured).slice(0, Math.min(3, max));

  return {
    title: fields.selected_title ?? "Selected projects",
    projects: featured,
  };
}

/** @deprecated Use getSelectedWorkProjectsFromSection */
export function getFeaturedWorkProjectsFromSection(section) {
  return getSelectedWorkProjectsFromSection(section);
}

export function splitParagraphs(text) {
  if (!text) return [];
  return String(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const POSITION_LIST_SUFFIXES = [
  "slug",
  "category",
  "title",
  "description",
  "location",
  "type",
];

const POSITION_DETAIL_SUFFIXES = [
  "intro",
  "role",
  "responsibilities_intro",
  "qualifications",
];

function parsePositionFromFields(data) {
  const responsibilities = [];
  for (let i = 0; i < 20; i++) {
    const item = data[`responsibility_${i}`];
    if (!item) break;
    responsibilities.push(item);
  }

  const position = {};
  POSITION_LIST_SUFFIXES.forEach((suffix) => {
    position[suffix] = data[suffix] ?? "";
  });
  POSITION_DETAIL_SUFFIXES.forEach((suffix) => {
    position[suffix] = data[suffix] ?? "";
  });
  position.responsibilities = responsibilities;

  return position;
}

/**
 * Open positions from position_N_* fields on a positions-type section.
 */
export function getPositionsFromSection(section) {
  if (!section?.fields) return [];
  const fields = section.fields;
  const byIndex = new Map();

  Object.keys(fields).forEach((key) => {
    const match = /^position_(\d+)_(.+)$/.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[suffix] = fields[key] ?? "";
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, data]) => parsePositionFromFields(data))
    .filter((position) => position.slug || position.title);
}

/**
 * Category filter labels from filter_N fields on the positions section.
 */
export function getPositionFiltersFromSection(section) {
  if (!section?.fields) return [];
  return Object.keys(section.fields)
    .filter((key) => /^filter_\d+$/.test(key))
    .sort(
      (a, b) =>
        Number(a.split("_")[1]) - Number(b.split("_")[1])
    )
    .map((key) => section.fields[key])
    .filter(Boolean);
}

/**
 * Culture gallery images from image_N fields.
 */
export function getCultureImagesFromSection(section) {
  if (!section?.fields) return [];
  const countRaw = Number(section.fields.image_count);
  const count = countRaw > 0 ? countRaw : 5;
  const images = [];

  for (let i = 0; i < count; i++) {
    const url = section.fields[`image_${i}`];
    if (url) images.push(url);
  }

  return images;
}

/**
 * Map pin locations from location_N_* fields on a contact-banner section.
 */
export function getContactLocationsFromSection(section) {
  if (!section?.fields) return [];
  const fields = section.fields;
  const byIndex = new Map();

  Object.keys(fields).forEach((key) => {
    const match = /^location_(\d+)_(.+)$/.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[suffix] = fields[key] ?? "";
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, data]) => ({
      id: index + 1,
      name: data.name ?? "",
      address: data.address ?? "",
      code: data.code ?? "",
      x: data.x ?? "",
      y: data.y ?? "",
    }))
    .filter((location) => location.name || location.x || location.y);
}

/**
 * Contact info columns from info_N_* fields on a contact-banner section.
 */
export function getContactInfoColumnsFromSection(section) {
  if (!section?.fields) return [];
  const fields = section.fields;
  const byIndex = new Map();

  Object.keys(fields).forEach((key) => {
    const match = /^info_(\d+)_(.+)$/.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[suffix] = fields[key] ?? "";
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, data]) => ({
      title: data.title ?? "",
      description: data.description ?? "",
      value: data.value ?? "",
    }))
    .filter((column) => column.title || column.value);
}
